import { logger } from '../lib/logger';

interface CaptureSlot<Recorder> {
  recordingId: string;
  recorder: Recorder;
}

interface DiscardReplacedSlotOptions<Recorder extends { discard: () => void }> {
  slot: CaptureSlot<Recorder> | null;
  replacedBy: string;
  eventType: string;
  beforeDiscard?: (recorder: Recorder) => void;
}

/** Loudly retires an unexpected live slot before its replacement is installed. */
export const discardReplacedCaptureSlot = <Recorder extends { discard: () => void }>({
  slot,
  replacedBy,
  eventType,
  beforeDiscard,
}: DiscardReplacedSlotOptions<Recorder>): void => {
  if (!slot) return;
  logger.error({
    event_type: eventType,
    recording_id: slot.recordingId,
    details: { replacedBy },
  });
  beforeDiscard?.(slot.recorder);
  slot.recorder.discard();
};

interface StopSlotOptions<Recorder extends { stop: () => Promise<void> }> {
  slot: CaptureSlot<Recorder> | null;
  expectedId: string | null | undefined;
  alreadyRetiredEvent: string;
  mismatchEvent: string;
  beforeStop?: (recorder: Recorder) => void;
  clear: () => void;
}

/** Idempotently stops a live slot, preserving its persist-bound tail. */
export const stopCaptureSlot = <Recorder extends { stop: () => Promise<void> }>({
  slot,
  expectedId,
  alreadyRetiredEvent,
  mismatchEvent,
  beforeStop,
  clear,
}: StopSlotOptions<Recorder>): Promise<void> => {
  if (!slot) {
    logger.info({ event_type: alreadyRetiredEvent, recording_id: expectedId });
    return Promise.resolve();
  }
  if (expectedId && slot.recordingId !== expectedId) {
    logger.warn({
      event_type: mismatchEvent,
      recording_id: expectedId,
      details: { slotRecordingId: slot.recordingId },
    });
  }
  beforeStop?.(slot.recorder);
  const tail = slot.recorder.stop();
  clear();
  return tail;
};
